<template>
    <div class="content-wrapper">
        <div class="row">
            <div class="col-sm-12 col-md-6">
                <app-breadcrumb :page-title="$t('real_time_attendance')" :icon="'activity'" />
            </div>
            <div class="col-sm-12 col-md-6 breadcrumb-side-button">
                <div class="float-md-right mb-3 mb-sm-3 mb-md-0">
                    <div class="btn-group mr-2">
                        <button 
                            type="button" 
                            class="btn btn-outline-primary"
                            :class="{ 'active': autoRefresh }"
                            @click="toggleAutoRefresh"
                        >
                            <i class="fas fa-sync-alt" :class="{ 'fa-spin': autoRefresh }"></i>
                            {{ $t('auto_refresh') }}
                        </button>
                    </div>
                    <button 
                        type="button" 
                        class="btn btn-primary btn-with-shadow"
                        @click="refreshData"
                        :disabled="loading"
                    >
                        <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
                        {{ $t('refresh_now') }}
                    </button>
                </div>
            </div>
        </div>

        <!-- Current Time Display -->
        <div class="row mb-4">
            <div class="col-12">
                <div class="card attendance-dark-hero text-white">
                    <div class="card-body text-center">
                        <h2 class="mb-1">{{ currentTime }}</h2>
                        <p class="mb-0">{{ currentDate }}</p>
                        <small class="text-muted">{{ $t('last_updated') }}: {{ lastUpdated }}</small>
                    </div>
                </div>
            </div>
        </div>

        <!-- Real-time Stats Cards -->
        <div class="row mb-4">
            <div class="col-lg-3 col-md-6 mb-3">
                <div class="card stat-card bg-gradient-primary text-white">
                    <div class="card-body">
                        <div class="d-flex justify-content-between align-items-center">
                            <div>
                                <h3 class="mb-0 counter" :data-count="realTimeStats.employees_currently_present || 0">
                                    {{ formatCountValue(realTimeStats.employees_currently_present || 0) }}
                                </h3>
                                <p class="mb-0">{{ $t('currently_present') }}</p>
                            </div>
                            <div class="stat-icon">
                                <i class="fas fa-users fa-2x"></i>
                            </div>
                        </div>
                        <div class="progress mt-2 attendance-progress">
                            <div class="progress-bar bg-white" :style="{ width: attendancePercentage + '%' }"></div>
                        </div>
                    </div>
                </div>
            </div>
            
            <div class="col-lg-3 col-md-6 mb-3">
                <div class="card stat-card bg-gradient-success text-white">
                    <div class="card-body">
                        <div class="d-flex justify-content-between align-items-center">
                            <div>
                                <h3 class="mb-0">{{ formatPercentValue(realTimeStats.attendance_rate || 0) }}%</h3>
                                <p class="mb-0">{{ $t('attendance_rate') }}</p>
                            </div>
                            <div class="stat-icon">
                                <i class="fas fa-chart-line fa-2x"></i>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <div class="col-lg-3 col-md-6 mb-3">
                <div class="card stat-card bg-gradient-warning text-white">
                    <div class="card-body">
                        <div class="d-flex justify-content-between align-items-center">
                            <div>
                                <h3 class="mb-0">{{ formatCountValue(recentCheckinsCount) }}</h3>
                                <p class="mb-0">{{ $t('recent_checkins') }}</p>
                                <small>{{ $t('last_30_minutes') }}</small>
                            </div>
                            <div class="stat-icon">
                                <i class="fas fa-sign-in-alt fa-2x"></i>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <div class="col-lg-3 col-md-6 mb-3">
                <div class="card stat-card bg-gradient-info text-white">
                    <div class="card-body">
                        <div class="d-flex justify-content-between align-items-center">
                            <div>
                                <h3 class="mb-0">{{ formatCountValue(pendingCheckoutsCount) }}</h3>
                                <p class="mb-0">{{ $t('pending_checkouts') }}</p>
                            </div>
                            <div class="stat-icon">
                                <i class="fas fa-clock fa-2x"></i>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Activity Timeline -->
        <div class="row mb-4">
            <div class="col-lg-8">
                <div class="card">
                    <div class="card-header d-flex justify-content-between align-items-center">
                        <h5 class="mb-0">{{ $t('recent_activity') }}</h5>
                        <div class="btn-group btn-group-sm">
                            <button 
                                type="button" 
                                class="btn btn-outline-primary"
                                :class="{ 'active': activityFilter === 'all' }"
                                @click="activityFilter = 'all'"
                            >
                                {{ $t('all') }}
                            </button>
                            <button 
                                type="button" 
                                class="btn btn-outline-success"
                                :class="{ 'active': activityFilter === 'checkins' }"
                                @click="activityFilter = 'checkins'"
                            >
                                {{ $t('checkins') }}
                            </button>
                            <button 
                                type="button" 
                                class="btn btn-outline-danger"
                                :class="{ 'active': activityFilter === 'checkouts' }"
                                @click="activityFilter = 'checkouts'"
                            >
                                {{ $t('checkouts') }}
                            </button>
                        </div>
                    </div>
                    <div class="card-body attendance-scroll-area">
                        <div class="timeline">
                            <div 
                                v-for="activity in filteredActivities" 
                                :key="activity.id"
                                class="timeline-item"
                                :class="activity.type === 'checkin' ? 'timeline-success' : 'timeline-danger'"
                            >
                                <div class="timeline-marker"></div>
                                <div class="timeline-content">
                                    <div class="d-flex justify-content-between align-items-start">
                                        <div>
                                            <h6 class="mb-1">{{ activity.employee_name }}</h6>
                                            <p class="mb-1 text-muted">{{ activity.location }}</p>
                                            <small class="text-muted">
                                                <i class="fas fa-map-marker-alt mr-1"></i>
                                                {{ activity.method }}
                                            </small>
                                        </div>
                                        <div class="text-right">
                                            <span class="badge" :class="activity.type === 'checkin' ? 'badge-success' : 'badge-danger'">
                                                <i class="fas" :class="activity.type === 'checkin' ? 'fa-sign-in-alt' : 'fa-sign-out-alt'"></i>
                                                {{ activity.time }}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div v-if="filteredActivities.length === 0" class="text-center text-muted py-4">
                                {{ $t('no_recent_activity') }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <div class="col-lg-4">
                <div class="card">
                    <div class="card-header">
                        <h5 class="mb-0">{{ $t('pending_checkouts') }}</h5>
                    </div>
                    <div class="card-body attendance-scroll-area">
                        <div class="list-group list-group-flush">
                            <div 
                                v-for="pending in realTimeStats.pending_checkouts" 
                                :key="pending.employee_id"
                                class="list-group-item d-flex justify-content-between align-items-center p-2"
                            >
                                <div>
                                    <strong>{{ pending.employee_name }}</strong><br>
                                    <small class="text-muted">
                                        {{ $t('checked_in_at') }}: {{ formatTime(pending.last_checkin) }}
                                    </small>
                                </div>
                                <div class="text-right">
                                    <span class="badge badge-warning">
                                        {{ calculateDuration(pending.last_checkin) }}
                                    </span>
                                </div>
                            </div>
                            <div v-if="!realTimeStats.pending_checkouts || realTimeStats.pending_checkouts.length === 0" 
                                 class="text-center text-muted py-4">
                                {{ $t('no_pending_checkouts') }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Quick Actions -->
        <div class="row">
            <div class="col-12">
                <div class="card">
                    <div class="card-header">
                        <h5 class="mb-0">{{ $t('quick_actions') }}</h5>
                    </div>
                    <div class="card-body">
                        <div class="row">
                            <div class="col-md-3 mb-3">
                                <button type="button" class="btn btn-outline-primary btn-block" @click="viewDashboard">
                                    <i class="fas fa-tachometer-alt mr-2"></i>
                                    {{ $t('view_dashboard') }}
                                </button>
                            </div>
                            <div class="col-md-3 mb-3">
                                <button type="button" class="btn btn-outline-success btn-block" @click="viewEmployeeDetails">
                                    <i class="fas fa-users mr-2"></i>
                                    {{ $t('employee_details') }}
                                </button>
                            </div>
                            <div class="col-md-3 mb-3">
                                <button type="button" class="btn btn-outline-info btn-block" @click="exportRealTimeReport">
                                    <i class="fas fa-file-export mr-2"></i>
                                    {{ $t('export_report') }}
                                </button>
                            </div>
                            <div class="col-md-3 mb-3">
                                <button type="button" class="btn btn-outline-warning btn-block" @click="sendAlerts">
                                    <i class="fas fa-bell mr-2"></i>
                                    {{ $t('send_alerts') }}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import CoreLibrary from '../../../../core/helpers/CoreLibrary';
import { ATTENDANCE_REAL_TIME_STATS, ATTENDANCE_EXPORT_SUMMARY } from '../../../Config/ApiUrl';

export default {
    extends: CoreLibrary,
    name: "RealTimeAttendance",
    data() {
        return {
            realTimeStats: {},
            loading: false,
            autoRefresh: true,
            refreshInterval: null,
            clockInterval: null,
            currentTime: '',
            currentDate: '',
            lastUpdated: '',
            activityFilter: 'all'
        }
    },
    computed: {
        attendancePercentage() {
            // Calculate percentage based on total employees vs currently present
            const total = this.realTimeStats.total_employees || 1;
            const present = this.realTimeStats.employees_currently_present || 0;
            return Math.round((present / total) * 100);
        },
        
        recentCheckinsCount() {
            return this.realTimeStats.recent_checkins ? this.realTimeStats.recent_checkins.length : 0;
        },
        
        pendingCheckoutsCount() {
            return this.realTimeStats.pending_checkouts ? this.realTimeStats.pending_checkouts.length : 0;
        },
        
        filteredActivities() {
            const checkins = (this.realTimeStats.recent_checkins || []).map(item => ({
                ...item,
                type: 'checkin',
                id: `checkin-${item.employee_name}-${item.time}`
            }));
            
            const checkouts = (this.realTimeStats.recent_checkouts || []).map(item => ({
                ...item,
                type: 'checkout',
                id: `checkout-${item.employee_name}-${item.time}`
            }));
            
            let activities = [...checkins, ...checkouts];
            
            if (this.activityFilter === 'checkins') {
                activities = checkins;
            } else if (this.activityFilter === 'checkouts') {
                activities = checkouts;
            }
            
            return activities.sort((a, b) => new Date(`2000-01-01 ${b.time}`) - new Date(`2000-01-01 ${a.time}`));
        }
    },
    mounted() {
        this.updateCurrentTime();
        this.refreshData();
        
        // Update current time every second
        this.clockInterval = setInterval(() => {
            this.updateCurrentTime();
        }, 1000);
        
        // Start auto refresh if enabled
        if (this.autoRefresh) {
            this.startAutoRefresh();
        }
    },
    beforeDestroy() {
        if (this.refreshInterval) {
            clearInterval(this.refreshInterval);
        }
        if (this.clockInterval) {
            clearInterval(this.clockInterval);
        }
    },
    methods: {
        async refreshData() {
            try {
                this.loading = true;
                const response = await this.axiosGet(ATTENDANCE_REAL_TIME_STATS);
                this.realTimeStats = response.data;
                this.lastUpdated = window.moment ? window.moment().format('HH:mm:ss') : new Date().toLocaleTimeString();
            } catch (error) {
                this.$toastr.e(this.$t('error_loading_realtime_stats'));
            } finally {
                this.loading = false;
            }
        },
        
        updateCurrentTime() {
            const now = window.moment ? window.moment() : new Date();
            this.currentTime = window.moment ? now.format('HH:mm:ss') : now.toLocaleTimeString();
            this.currentDate = window.moment ? now.format('dddd, MMMM Do YYYY') : now.toLocaleDateString();
        },
        
        toggleAutoRefresh() {
            this.autoRefresh = !this.autoRefresh;
            
            if (this.autoRefresh) {
                this.startAutoRefresh();
            } else {
                this.stopAutoRefresh();
            }
        },
        
        startAutoRefresh() {
            if (this.refreshInterval) {
                clearInterval(this.refreshInterval);
            }
            this.refreshInterval = setInterval(() => {
                this.refreshData();
            }, 30000); // Refresh every 30 seconds
        },
        
        stopAutoRefresh() {
            if (this.refreshInterval) {
                clearInterval(this.refreshInterval);
                this.refreshInterval = null;
            }
        },
        
        formatTime(timeString) {
            return window.moment ? window.moment(timeString).format('HH:mm:ss') : new Date(timeString).toLocaleTimeString();
        },
        
        calculateDuration(checkinTime) {
            if (window.moment) {
                const now = window.moment();
                const checkin = window.moment(checkinTime);
                const duration = window.moment.duration(now.diff(checkin));
                
                const hours = Math.floor(duration.asHours());
                const minutes = duration.minutes();
                
                return `${hours}h ${minutes}m`;
            } else {
                const now = new Date();
                const checkin = new Date(checkinTime);
                const diffMs = now - checkin;
                const hours = Math.floor(diffMs / (1000 * 60 * 60));
                const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
                
                return `${hours}h ${minutes}m`;
            }
        },
        
        viewDashboard() {
            window.location.href = '/attendance/dashboard-summary';
        },
        
        viewEmployeeDetails() {
            window.location.href = '/attendance/employee-details';
        },
        
        exportRealTimeReport() {
            const url = ATTENDANCE_EXPORT_SUMMARY;
            window.open(url, '_blank');
        },
        
        sendAlerts() {
            // Implementation for sending alerts to managers
            this.$toastr.i(this.$t('alerts_sent_successfully'));
        }
    }
}
</script>

<style scoped>
.stat-card {
    border: none;
    border-radius: var(--mozali-radius-lg);
    overflow: hidden;
    box-shadow: var(--mozali-shadow-card);
    transition: transform var(--mozali-motion-micro) var(--mozali-motion-ease),
    box-shadow var(--mozali-motion-standard) var(--mozali-motion-ease);
}

.stat-card:hover {
    transform: translateY(-4px);
    box-shadow: var(--mozali-shadow-card-hover);
}

.stat-icon {
    opacity: 0.8;
}

.bg-gradient-primary {
    background: var(--mozali-brand-gradient);
}

.bg-gradient-success {
    background: linear-gradient(135deg, var(--mozali-color-brand-dark) 0%, var(--mozali-color-cyan) 100%);
}

.bg-gradient-warning {
    background: linear-gradient(135deg, var(--mozali-color-indigo) 0%, var(--mozali-color-brand-dark) 100%);
}

.bg-gradient-info {
    background: var(--mozali-dark-gradient);
}

.attendance-dark-hero {
    background: var(--mozali-dark-gradient);
    border: 1px solid rgba(59, 130, 246, 0.32);
}

.attendance-scroll-area {
    max-height: 400px;
    overflow-y: auto;
}

.attendance-progress {
    height: 4px;
}

.timeline {
    position: relative;
    padding-left: 30px;
}

.timeline-item {
    position: relative;
    margin-bottom: 20px;
}

.timeline-item::before {
    content: '';
    position: absolute;
    left: -36px;
    top: 0;
    bottom: -20px;
    width: 2px;
    background: var(--default-border-color);
}

.timeline-item:last-child::before {
    display: none;
}

.timeline-marker {
    position: absolute;
    left: -42px;
    top: 5px;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    border: 2px solid var(--default-card-bg);
    box-shadow: 0 0 0 2px var(--default-border-color);
}

.timeline-success .timeline-marker {
    background-color: var(--mozali-color-cyan);
    box-shadow: 0 0 0 2px var(--mozali-color-cyan);
}

.timeline-danger .timeline-marker {
    background-color: var(--mozali-color-brand-dark);
    box-shadow: 0 0 0 2px var(--mozali-color-brand-dark);
}

.timeline-content {
    background: var(--base-color);
    border-radius: var(--mozali-radius-sm);
    padding: 15px;
    border-left: 3px solid var(--default-border-color);
}

.timeline-success .timeline-content {
    border-left-color: var(--mozali-color-cyan);
}

.timeline-danger .timeline-content {
    border-left-color: var(--mozali-color-brand-dark);
}

.counter {
    font-weight: bold;
    font-size: 2rem;
}

.progress {
    background-color: rgba(255, 255, 255, 0.2);
}

.btn.active {
    background-color: var(--mozali-color-brand);
    border-color: var(--mozali-color-brand);
    color: white;
}

@media (prefers-reduced-motion: reduce) {
    .stat-card {
        transition: none !important;
    }

    .stat-card:hover {
        transform: none !important;
    }
}
</style>
